import React, { useEffect, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import {
    Check,
    Copy,
    Download,
    KeyRound,
    Pencil,
    Plus,
    RefreshCw,
    RotateCcw,
    Trash2,
    TriangleAlert,
} from "lucide-react";
import { mflQrOptions } from "./fighter-qr";
import { JUDGE_PATH } from "../App";
import { buildJudgeUrl, judgePresence, seenAgoText } from "../utils/judge-access";
import {
    addEventJudge,
    JUDGE_NAME_MAX_LENGTH,
    regenerateJudgePin,
    removeEventJudge,
    renameEventJudge,
    rotateJudgeLink,
    setJudgeRevoked,
    setupEventJudging,
    subscribeJudgeLink,
} from "../utils/firebase";

// Rendered larger than it is shown so the downloaded PNG survives printing.
const QR_EXPORT_SIZE = 600;

const slugify = (value) =>
    String(value ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

// LIVE: the judge page was on screen in the last 2 minutes. AWAY: signed in,
// but the page is closed, in the background or the phone is asleep.
const JudgeBadge = ({ presence, isEnded }) => {
    if (presence.state === "revoked") {
        return <span className="badge badge-error badge-sm">REVOKED</span>;
    }
    if (presence.state === "not_opened") {
        return <span className="badge badge-ghost badge-sm">NOT OPENED</span>;
    }
    // After END EVENT the judge page stops calling in, so everyone would read AWAY.
    if (isEnded) {
        return <span className="badge badge-ghost badge-sm">SIGNED IN</span>;
    }
    if (presence.state === "online") {
        return <span className="badge badge-success badge-sm">LIVE</span>;
    }
    return <span className="badge badge-warning badge-sm">AWAY</span>;
};

/**
 * The judge's name, typed straight into the row. Saves on Enter or when the
 * field loses focus; Escape puts the saved name back. An invalid or clashing
 * name is refused with a toast and the field reverts.
 */
const JudgeNameField = ({ eventId, judge, disabled }) => {
    const [draft, setDraft] = useState(judge.name ?? "");
    const [saving, setSaving] = useState(false);
    const focused = useRef(false);

    // Follow live changes (another admin, or our own save landing) unless
    // the admin is mid-edit.
    useEffect(() => {
        if (!focused.current) setDraft(judge.name ?? "");
    }, [judge.name]);

    const save = async () => {
        const next = draft.replace(/\s+/g, " ").trim();
        if (next === (judge.name ?? "")) {
            setDraft(judge.name ?? "");
            return;
        }
        setSaving(true);
        try {
            await renameEventJudge(eventId, judge.id, next);
            toast.success(`Renamed to ${next}`);
        } catch (error) {
            toast.error(error?.message || "Couldn't rename the judge");
            setDraft(judge.name ?? "");
        } finally {
            setSaving(false);
        }
    };

    return (
        <label className="relative flex items-center flex-1 min-w-[8rem] max-w-[16rem]">
            <span className="sr-only">Judge name</span>
            <input
                type="text"
                value={draft}
                maxLength={JUDGE_NAME_MAX_LENGTH}
                disabled={disabled || saving}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={(e) => {
                    focused.current = true;
                    e.target.select();
                }}
                onBlur={() => {
                    focused.current = false;
                    save();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                    if (e.key === "Escape") {
                        setDraft(judge.name ?? "");
                        // Blur with the old name back in place: nothing to save.
                        requestAnimationFrame(() => e.target.blur());
                    }
                }}
                placeholder="Judge name"
                className="input input-sm input-bordered w-full pr-8 font-bold text-white disabled:text-white disabled:bg-transparent disabled:border-transparent disabled:px-0"
            />
            {!disabled &&
                (saving ? (
                    <span className="loading loading-spinner loading-xs absolute right-2.5" />
                ) : (
                    <Pencil
                        size={14}
                        className="absolute right-2.5 opacity-40 pointer-events-none"
                        aria-hidden="true"
                    />
                ))}
        </label>
    );
};

const millisOf = (value) =>
    value && typeof value.toMillis === "function" ? value.toMillis() : 0;

/**
 * Admin "Judges" tab on Manage Event: the event's judge link (QR + URL) and
 * one row per judge with their PIN and how many cards they've submitted.
 *
 * Everything shown is live: the judges list comes from Manage Event's
 * snapshot listener, the link from one here, so a judge signing in (LIVE
 * badge), going quiet (AWAY) or the wrong-PIN lock tripping shows up
 * without a reload.
 *
 * @param {object}   props
 * @param {string}   props.eventId
 * @param {string}   [props.eventName]   used in the PNG filename
 * @param {boolean}  [props.isEnded]     read-only when true
 * @param {Array|null} props.judges      the event's judges; null while loading
 * @param {boolean}  [props.judgesError] the judges listener failed
 * @param {Array}    [props.scorecards]  judge-scorecards for the event
 * @param {Array}    [props.boutKeys]    the bouts on the card right now
 */
export default function EventJudgesPanel({
    eventId,
    eventName,
    isEnded = false,
    judges,
    judgesError = false,
    scorecards = [],
    boutKeys = [],
}) {
    // undefined while loading, null when judging isn't set up yet.
    const [link, setLink] = useState(undefined);
    const [linkError, setLinkError] = useState(false);
    const [now, setNow] = useState(Date.now());
    // Which action is in flight: "setup" | "rotate" | "add" | "pin:<id>" | "revoke:<id>"
    const [busy, setBusy] = useState(null);
    const [copied, setCopied] = useState(false);
    const [qrCode, setQrCode] = useState(null);

    const qrRef = useRef(null);
    const rotateDialog = useRef(null);
    const removeDialog = useRef(null);
    // The judge the remove dialog is asking about.
    const [removing, setRemoving] = useState(null);
    const copiedTimer = useRef(null);

    const judgeUrl = link?.token
        ? buildJudgeUrl(window.location.origin, link.token, JUDGE_PATH)
        : "";

    useEffect(() => {
        if (!eventId) return undefined;
        setLink(undefined);
        setLinkError(false);
        return subscribeJudgeLink(eventId, setLink, () => setLinkError(true));
    }, [eventId]);

    // The wrong-PIN lock expires on its own; tick so the banner goes away.
    const lockedUntil = millisOf(link?.lockedUntil);
    const locked = lockedUntil > now;
    useEffect(() => {
        if (!locked) return undefined;
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, [locked]);

    // LIVE turns to AWAY by the clock, not by a write, so tick while anyone
    // is signed in.
    const watching =
        !isEnded && !!judges?.some((j) => j.lastRedeemedAt && !j.revokedAt);
    useEffect(() => {
        if (!watching) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 15000);
        return () => clearInterval(id);
    }, [watching]);

    // Judges with any card at all, drafts and bouts since pulled included:
    // they keep their record and can only be revoked.
    const hasCards = new Set(scorecards.map((c) => c.judgeId));

    // Submitted cards per judge, counting only bouts still on the card.
    const submittedByJudge = {};
    const onCard = new Set(boutKeys);
    scorecards.forEach((c) => {
        if (c.submitted && onCard.has(c.boutKey)) {
            submittedByJudge[c.judgeId] = (submittedByJudge[c.judgeId] ?? 0) + 1;
        }
    });

    useEffect(() => () => clearTimeout(copiedTimer.current), []);

    useEffect(() => {
        setQrCode(
            judgeUrl
                ? new QRCodeStyling(mflQrOptions(judgeUrl, QR_EXPORT_SIZE))
                : null
        );
    }, [judgeUrl]);

    // The QR box only exists once the link card renders, and unmounts again
    // on reload/retry, so re-attach whenever it (re)appears.
    const linkCardShown = !!link && !!judges;
    useEffect(() => {
        const el = qrRef.current;
        if (!el) return;
        el.innerHTML = "";
        if (qrCode) qrCode.append(el);
    }, [qrCode, linkCardShown]);

    // Runs one write with a busy flag and a toast. The snapshot listeners
    // pick up the change, so nothing is patched locally.
    const run = async (key, action, messages) => {
        setBusy(key);
        try {
            const result = await toast.promise(action(), {
                loading: messages.loading,
                success: messages.success,
                error: (error) => error?.message || messages.error,
            });
            return result;
        } catch (error) {
            console.error(`Error on ${key}: `, error);
            return undefined;
        } finally {
            setBusy(null);
        }
    };

    const handleSetup = () =>
        run("setup", () => setupEventJudging(eventId, 3), {
            loading: "Setting up judges",
            success: "Judge link and 3 judges created",
            error: "Couldn't set up judges",
        });

    const handleConfirmRotate = () => {
        rotateDialog.current?.close();
        return run("rotate", () => rotateJudgeLink(eventId), {
            loading: "Replacing judge link",
            success: "New judge link ready. The old one no longer works.",
            error: "Couldn't replace the judge link",
        });
    };

    const handleAdd = () =>
        run("add", () => addEventJudge(eventId), {
            loading: "Adding judge",
            success: (j) => `${j.name} added`,
            error: "Couldn't add a judge",
        });

    const handleNewPin = (judge) =>
        run(
            `pin:${judge.id}`,
            () => regenerateJudgePin(eventId, judge.id),
            {
                loading: `New PIN for ${judge.name}`,
                success: (r) => `${judge.name}'s PIN is now ${r.pin}. Their device is signed out.`,
                error: "Couldn't change the PIN",
            }
        );

    const askRemove = (judge) => {
        setRemoving(judge);
        removeDialog.current?.showModal();
    };

    const handleConfirmRemove = () => {
        removeDialog.current?.close();
        const judge = removing;
        if (!judge) return undefined;
        return run(`remove:${judge.id}`, () => removeEventJudge(eventId, judge.id), {
            loading: `Removing ${judge.name}`,
            success: `${judge.name} removed`,
            error: "Couldn't remove the judge",
        });
    };

    const handleToggleRevoke = (judge) => {
        const revoke = !judge.revokedAt;
        return run(
            `revoke:${judge.id}`,
            () => setJudgeRevoked(judge.id, revoke),
            {
                loading: revoke
                    ? `Revoking ${judge.name}`
                    : `Restoring ${judge.name}`,
                success: revoke
                    ? `${judge.name} revoked. Their PIN no longer works.`
                    : `${judge.name} restored`,
                error: revoke ? "Couldn't revoke" : "Couldn't restore",
            }
        );
    };

    const handleCopy = async () => {
        if (!judgeUrl) return;
        try {
            await navigator.clipboard.writeText(judgeUrl);
            toast("📋 Copied judge link to Clipboard");
            setCopied(true);
            clearTimeout(copiedTimer.current);
            copiedTimer.current = setTimeout(() => setCopied(false), 1200);
        } catch (error) {
            console.error("Error copying judge link: ", error);
            toast.error("Couldn't copy. Select the link and copy it by hand.");
        }
    };

    const handleDownload = () => {
        if (!qrCode) return;
        const slug = slugify(eventName) || eventId;
        qrCode.download({ extension: "png", name: `${slug}-judge-qr` });
    };

    if (linkError || judgesError) {
        return (
            <div className="w-full h-full flex-center">
                <div className="flex flex-col items-center gap-4">
                    <p>Couldn't load judges for this event.</p>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => window.location.reload()}
                    >
                        <RefreshCw size={16} />
                        Reload
                    </button>
                </div>
            </div>
        );
    }

    if (!judges || link === undefined) {
        return (
            <div className="w-full h-full flex-center">
                <div className="loading loading-lg loading-bars" />
            </div>
        );
    }

    if (!link) {
        return (
            <div className="w-full h-full flex-center">
                <div className="flex flex-col items-center gap-4 text-center max-w-sm">
                    {isEnded ? (
                        <p>No judges were set up for this event.</p>
                    ) : (
                        <>
                            <p>No judges set up for this event yet.</p>
                            <p className="text-sm opacity-70">
                                Creates one judge link for the event and three
                                judges, each with their own 4-digit PIN.
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={handleSetup}
                                disabled={busy === "setup"}
                            >
                                {busy === "setup" ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <KeyRound size={20} />
                                )}
                                Set up judges
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full overflow-y-auto">
            <div className="grid grid-cols-1 2xl:grid-cols-[18rem_minmax(0,1fr)] gap-4 items-start">
                {/* Link card */}
                <div className="border p-4 flex flex-col gap-3">
                    <p className="text-xs font-bold uppercase tracking-widest opacity-60">
                        Judge link
                    </p>
                    <div
                        ref={qrRef}
                        aria-label="Judge link QR code"
                        role="img"
                        className="w-full max-w-[11rem] mx-auto aspect-square bg-white overflow-hidden [&>*]:!w-full [&>*]:!h-full [&>*]:block"
                    />
                    <input
                        className="input input-bordered input-sm w-full font-mono"
                        readOnly
                        value={judgeUrl}
                        aria-label="Judge link"
                        onFocus={(e) => e.target.select()}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={handleCopy}
                        >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                        <button
                            className="btn btn-sm btn-outline"
                            onClick={handleDownload}
                            disabled={!qrCode}
                        >
                            <Download size={16} />
                            PNG
                        </button>
                        {!isEnded && (
                            <button
                                className="btn btn-sm btn-outline btn-error col-span-2"
                                onClick={() => rotateDialog.current?.showModal()}
                                disabled={busy === "rotate"}
                            >
                                {busy === "rotate" ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <RefreshCw size={16} />
                                )}
                                New link
                            </button>
                        )}
                    </div>
                    <p className="text-xs opacity-60">
                        Judges open this link, then sign in with their own PIN.
                    </p>
                    {locked && (
                        <div
                            role="alert"
                            className="border border-error text-error p-3 flex gap-2 text-sm"
                        >
                            <TriangleAlert size={18} className="flex-none mt-0.5" />
                            <p>
                                <strong>Sign-in locked</strong> until{" "}
                                {new Date(lockedUntil).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                                : {link.lockedMisses ?? 30} wrong PINs on this
                                link within an hour. Judges already signed in
                                can keep scoring.
                                {!isEnded && " New link lifts the lock."}
                            </p>
                        </div>
                    )}
                </div>

                {/* Judges */}
                <div className="flex flex-col gap-3">
                    {judges.length === 0 && (
                        <p className="text-sm opacity-70">
                            No judges on this event.
                        </p>
                    )}
                    {judges.map((judge, index) => {
                        const pinBusy = busy === `pin:${judge.id}`;
                        const revokeBusy =
                            busy === `revoke:${judge.id}` ||
                            busy === `remove:${judge.id}`;
                        // Never scored: nothing to keep, so remove outright.
                        const removable = !hasCards.has(judge.id);
                        const presence = judgePresence(judge, now);
                        return (
                            <div
                                key={judge.id}
                                className={twMerge(
                                    "border p-4 pl-12 relative grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-3",
                                    judge.revokedAt ? "opacity-50" : ""
                                )}
                            >
                                <p className="absolute top-0 left-0 font-bold text-sm p-2 border w-8 h-8 flex-center -translate-x-[1px] -translate-y-[1px] leading-none">
                                    {index + 1}
                                </p>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                        <JudgeNameField
                                            eventId={eventId}
                                            judge={judge}
                                            disabled={isEnded}
                                        />
                                        <JudgeBadge presence={presence} isEnded={isEnded} />
                                    </div>
                                    <p className="text-xs opacity-60 mt-1 tabular-nums">
                                        {submittedByJudge[judge.id] ?? 0} / {boutKeys.length}{" "}
                                        {boutKeys.length === 1 ? "match" : "matches"} submitted
                                    </p>
                                    {presence.seenAt && presence.state !== "revoked" && (
                                        <p
                                            className={twMerge(
                                                "text-xs tabular-nums",
                                                presence.state === "away" && !isEnded
                                                    ? "text-warning"
                                                    : "opacity-60"
                                            )}
                                        >
                                            {presence.state === "online" && !isEnded
                                                ? "On the judge page now"
                                                : `Last seen ${seenAgoText(presence.seenAt, now)}`}
                                        </p>
                                    )}
                                </div>
                                <span
                                    className="font-mono text-2xl font-bold tracking-[0.2em] text-white border border-base-content/20 py-0.5 pl-[calc(0.5rem+0.2em)] pr-2 tabular-nums"
                                    aria-label={`PIN for ${judge.name}`}
                                >
                                    {judge.pin}
                                </span>
                                {!isEnded && (
                                    <div className="flex gap-2 col-span-2 lg:col-span-1">
                                        <button
                                            className="btn btn-sm"
                                            onClick={() => handleNewPin(judge)}
                                            disabled={pinBusy || revokeBusy}
                                        >
                                            {pinBusy ? (
                                                <span className="loading loading-spinner loading-xs" />
                                            ) : (
                                                <RotateCcw size={16} />
                                            )}
                                            New PIN
                                        </button>
                                        {/* A revoked judge who never scored can still be restored. */}
                                        {(!removable || judge.revokedAt) && (
                                            <button
                                                className="btn btn-sm btn-outline btn-error"
                                                onClick={() =>
                                                    handleToggleRevoke(judge)
                                                }
                                                disabled={pinBusy || revokeBusy}
                                            >
                                                {busy === `revoke:${judge.id}` ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : null}
                                                {judge.revokedAt
                                                    ? "Restore"
                                                    : "Revoke"}
                                            </button>
                                        )}
                                        {removable && (
                                            <button
                                                className="btn btn-sm btn-outline btn-error"
                                                onClick={() => askRemove(judge)}
                                                disabled={pinBusy || revokeBusy}
                                            >
                                                {busy === `remove:${judge.id}` ? (
                                                    <span className="loading loading-spinner loading-xs" />
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {!isEnded && (
                        <button
                            className="btn btn-primary"
                            onClick={handleAdd}
                            disabled={busy === "add"}
                        >
                            {busy === "add" ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <Plus size={20} />
                            )}
                            Add judge
                        </button>
                    )}
                </div>
            </div>

            <dialog ref={rotateDialog} className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">Replace the judge link?</h3>
                    <p className="py-4">
                        The current link and QR code stop working straight
                        away, including any you have already printed or sent.
                        Judges will need the new link. PINs stay the same.
                    </p>
                    <div className="modal-action">
                        <form method="dialog">
                            <button className="btn btn-ghost">Cancel</button>
                        </form>
                        <button
                            className="btn btn-error"
                            onClick={handleConfirmRotate}
                        >
                            New link
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            <dialog
                ref={removeDialog}
                className="modal"
                onClose={() => setRemoving(null)}
            >
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">
                        Remove {removing?.name}?
                    </h3>
                    <p className="py-4">
                        They haven't scored anything yet, so nothing is lost.
                        PIN {removing?.pin} stops working straight away.
                    </p>
                    <div className="modal-action">
                        <form method="dialog">
                            <button className="btn btn-ghost">Cancel</button>
                        </form>
                        <button
                            className="btn btn-error"
                            onClick={handleConfirmRemove}
                        >
                            Remove
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </div>
    );
}
